import type { Register } from '../profile/register.ts';

/** Whether the microphone is currently capturing an utterance. */
export type GestureState = { capturing: boolean };

export type GestureEvent = 'down' | 'up' | 'cancel';

/**
 * The mic gesture, per D5: hold-to-talk for adults, tap-to-talk for children.
 *
 * A reducer rather than two components, because the transport underneath is
 * identical — the same `beginUtterance` and the same `endUtterance`. Only the
 * question "does releasing end it?" differs, and that is one branch here
 * instead of a second copy of the mic control to keep in step.
 */
export function gestureReducer(register: Register, state: GestureState, event: GestureEvent): GestureState {
  // Whatever the register: a `pointercancel` means the gesture was taken away
  // — a touch-scroll, a system gesture — and capturing for ever is never the
  // right response to that.
  if (event === 'cancel') return { capturing: false };

  if (register === 'kid') {
    // Tap to start, tap to stop. `up` is deliberately inert: a child who
    // presses and releases has started talking, not finished.
    return event === 'down' ? { capturing: !state.capturing } : state;
  }

  if (event === 'down') return { capturing: true };
  // An `up` with nothing captured is a pointer that went down somewhere else.
  return state.capturing ? { capturing: false } : state;
}

/**
 * The keys that work the microphone, and what a key event from one means.
 *
 * §11 requires the mic control to be keyboard-operable in both gestures, and
 * it is the only control in the product — a page where it can only be reached
 * with a pointer is unusable for anyone driving by keyboard or switch.
 *
 * `Enter` and `Space` because those are the two a native `<button>` already
 * answers to; adding our own key would be a second thing to discover.
 *
 * `repeat` is the one that matters. Holding a key down makes the platform
 * emit `keydown` over and over, and in the kid register every one of those is
 * a tap — the microphone would open and shut many times a second for as long
 * as a child leant on the key. The repeats are the operating system talking,
 * not the speaker, so they are dropped here rather than in the reducer, which
 * is about registers and not about input devices.
 *
 * Returns `null` for anything that should not move the gesture at all.
 */
export function keyGesture(key: string, kind: 'down' | 'up', repeat: boolean): GestureEvent | null {
  if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') return null;
  if (repeat) return null;
  return kind;
}
